import { useCallback, useEffect, useState } from 'react'
import { usePrincipal } from '../api/hooks'
import type { RunConfig } from '../api/types'

/** The name shown for the catalogue's own defaults. */
export const BUILT_IN_PRESET = 'EUDR default'

interface Saved {
  config: RunConfig
  preset: string
}

/**
 * Laid over the catalogue's defaults, so a check added since it was saved
 * starts at its default and one since removed is dropped.
 */
function merge(saved: Partial<RunConfig> | undefined, defaults: RunConfig): RunConfig {
  return {
    checks: Object.fromEntries(
      Object.entries(defaults.checks).map(([key, value]) => [key, saved?.checks?.[key] ?? value]),
    ),
    // Never the saved ones: the API checks every run against the default
    // layers, and a layer id kept from before would only mislead.
    layers: defaults.layers,
  }
}

function read(key: string | null, defaults: RunConfig): Saved {
  const fallback = { config: defaults, preset: BUILT_IN_PRESET }
  if (!key) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    const saved = JSON.parse(raw) as Partial<Saved>
    return { config: merge(saved.config, defaults), preset: saved.preset ?? BUILT_IN_PRESET }
  } catch {
    return fallback
  }
}

/**
 * The checks and parameters chosen for the next run, kept in this browser
 * for the signed-in user. They lived only in the screen's state, so leaving
 * Add polygons and coming back put every change back to the defaults
 * (AG-77). Drawing reads the same, so drawn plots are checked the same way.
 */
export function useRunConfig(defaults: RunConfig) {
  const { data: principal } = usePrincipal()
  const user = principal?.user
  const key = user ? `pc:run-config:${user.organisation_id}:${user.id}` : null
  const [saved, setSaved] = useState<Saved>(() => read(key, defaults))

  // The session can arrive after the first render.
  useEffect(() => {
    setSaved(read(key, defaults))
    // `defaults` comes from a cached query and only matters with a new key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const save = useCallback(
    (config: RunConfig, preset: string) => {
      setSaved({ config, preset })
      if (!key) return
      try {
        window.localStorage.setItem(key, JSON.stringify({ config, preset }))
      } catch {
        // Private windows and full storage: it still works for this visit.
      }
    },
    [key],
  )

  return { config: saved.config, preset: saved.preset, save }
}
